import { useState, useEffect, useCallback, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Heart, Users, HandHeart, Building2, BookOpen, ShieldCheck, Loader2, AlertCircle, Plus, X, CheckCircle2 } from 'lucide-react'
import { PortalLayout } from '../../components/portal/PortalLayout'
import { Seo } from '../../components/portal/Seo'
import { Container, Section, SectionHeading, Breadcrumbs, Card, Badge, EmptyState, FormField } from '../../components/portal/ui'
import { useModalA11y } from '../../components/portal/useModalA11y'
import {
  listTestimonials,
  submitTestimonial,
  TESTIMONIAL_ROLE_LABELS,
  type Testimonial,
  type TestimonialRole,
  type TestimonialRequest,
} from '../../services/testimonialService'
import { TranslatedContentNotice } from '../../components/portal/TranslatedContentNotice'

const CATEGORIES = [
  { key: 'all', label: 'All stories', icon: BookOpen, role: undefined as TestimonialRole | undefined },
  { key: 'PATIENT', label: 'Patient stories', icon: Heart, role: 'PATIENT' as TestimonialRole },
  { key: 'SURVIVOR', label: 'Survivor stories', icon: Heart, role: 'SURVIVOR' as TestimonialRole },
  { key: 'NIKSHAY_MITRA', label: 'TB Supporter stories', icon: HandHeart, role: 'NIKSHAY_MITRA' as TestimonialRole },
  { key: 'VOLUNTEER', label: 'Volunteer stories', icon: Users, role: 'VOLUNTEER' as TestimonialRole },
  { key: 'CSR_PARTNER', label: 'CSR stories', icon: Building2, role: 'CSR_PARTNER' as TestimonialRole },
]
const ROLES = Object.keys(TESTIMONIAL_ROLE_LABELS) as TestimonialRole[]
const size = 12

export default function StoriesPage() {
  const { t, i18n } = useTranslation()
  const [items, setItems] = useState<Testimonial[]>([])
  const [cat, setCat] = useState('all')
  const [page, setPage] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [shareOpen, setShareOpen] = useState(false)

  const activeRole = CATEGORIES.find((c) => c.key === cat)?.role
  const catLabel = (key: string) =>
    t(`pages.stories.categories.${key}`, { defaultValue: CATEGORIES.find((c) => c.key === key)?.label ?? key })

  const load = useCallback(async () => {
    setLoading(true); setError(null)
    try {
      const data = await listTestimonials({ role: activeRole, page, size })
      setItems(data.content); setTotal(data.totalElements); setTotalPages(data.totalPages)
    } catch (e: any) {
      setError(e?.response?.data?.message || t('pages.stories.error'))
    } finally { setLoading(false) }
  }, [activeRole, page, t])

  useEffect(() => { load() }, [load])
  useEffect(() => { setPage(0) }, [cat])

  return (
    <PortalLayout>
      <Seo
        title={t('pages.stories.seo.title')}
        description={t('pages.stories.seo.description')}
        canonicalPath="/stories"
        breadcrumbs={[{ name: t('common.home', 'Home'), path: '/' }, { name: t('nav.stories', 'Stories'), path: '/stories' }]}
      />
      <Section className="pb-6">
        <Container>
          <Breadcrumbs items={[{ name: t('common.home', 'Home'), path: '/' }, { name: t('nav.stories', 'Stories') }]} />
          <SectionHeading as="h1" eyebrow={t('pages.stories.hero.eyebrow')} title={t('pages.stories.hero.title')} description={t('pages.stories.hero.description')} />
          {/* Privacy note — demoted from a big Alert to a subtle line, so the stories lead. */}
          <p className="text-xs text-snow-600 max-w-2xl">
            {t('pages.stories.hero.consentNote')}
          </p>
        </Container>
      </Section>

      <Section className="bg-white pt-4">
        <Container>
          <div className="flex flex-wrap gap-2 mb-6">
            {CATEGORIES.map((c) => {
              const Icon = c.icon
              return (
                <button key={c.key} onClick={() => setCat(c.key)} aria-pressed={cat === c.key}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition ${cat === c.key ? 'bg-[rgb(15_76_129)] text-white' : 'bg-snow-100 text-snow-700 hover:bg-snow-200'}`}>
                  <Icon className="w-4 h-4" aria-hidden="true" /> {catLabel(c.key)}
                </button>
              )
            })}
          </div>

          <p className="text-sm text-snow-500 mb-4" aria-live="polite">{loading ? t('pages.stories.loading') : t('pages.stories.count', { count: total })}</p>

          {error ? (
            <div className="flex items-center gap-2 p-4 bg-red-50 text-red-700 rounded-lg text-sm" role="alert"><AlertCircle className="w-4 h-4" /> {error}</div>
          ) : loading ? (
            <div className="flex items-center justify-center py-16 text-snow-500"><Loader2 className="w-6 h-6 animate-spin mr-2" /> {t('pages.stories.loadingStories')}</div>
          ) : items.length === 0 ? (
            <EmptyState
              icon={<Heart className="w-7 h-7" />}
              title={t('pages.stories.empty.title')}
              description={t('pages.stories.empty.desc')}
              action={<button onClick={() => setShareOpen(true)} className="btn-primary btn-sm">{t('pages.stories.empty.cta')}</button>}
            />
          ) : (
            <>
              {items.some((tm) => tm.matchedLanguage && tm.matchedLanguage !== (i18n.language?.split('-')[0] || 'en')) && (
                <TranslatedContentNotice matchedLanguage="en" />
              )}
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {items.map((tm) => (
                  <Card key={tm.id} className="p-5 flex flex-col">
                    <div className="flex items-center gap-2 mb-2">
                      {tm.authorRole && <Badge tone="green"><ShieldCheck className="w-3 h-3" aria-hidden="true" /> {t(`pages.stories.roles.${tm.authorRole}`, { defaultValue: TESTIMONIAL_ROLE_LABELS[tm.authorRole] })}</Badge>}
                      {tm.isFeatured && <Badge tone="teal">{t('pages.stories.featured')}</Badge>}
                    </div>
                    <h3 className="font-semibold text-snow-900">{tm.title}</h3>
                    <p className="text-sm text-snow-600 mt-2 line-clamp-4 flex-1">{tm.body}</p>
                    <p className="text-xs text-snow-500 mt-3">
                      {tm.isAnonymous ? t('common.sharedAnonymously') : (tm.authorName ?? t('pages.stories.roles.OTHER', { defaultValue: 'A community member' }))}
                      {tm.publishedAt && ` · ${new Date(tm.publishedAt).toLocaleDateString()}`}
                    </p>
                  </Card>
                ))}
              </div>
              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-8">
                  <button onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0} className="btn-outline btn-sm disabled:opacity-50">{t('common.previous')}</button>
                  <span className="text-xs text-snow-500">{t('common.pageXofY', { page: page + 1, total: totalPages })}</span>
                  <button onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1} className="btn-outline btn-sm disabled:opacity-50">{t('common.next')}</button>
                </div>
              )}
            </>
          )}
        </Container>
      </Section>

      <Section className="bg-snow-50">
        <Container>
          <Card className="p-8 text-center bg-gradient-to-br from-[rgb(232_241_252)] to-white">
            <h2 className="text-xl font-bold text-snow-900">{t('pages.stories.shareCta.title')}</h2>
            <p className="text-snow-600 mt-2 max-w-xl mx-auto">{t('pages.stories.shareCta.body')}</p>
            <div className="mt-4"><button onClick={() => setShareOpen(true)} className="btn-primary">{t('pages.stories.shareCta.button')}</button></div>
          </Card>
        </Container>
      </Section>

      {shareOpen && <ShareStoryModal onClose={() => { setShareOpen(false); load() }} />}
    </PortalLayout>
  )
}

function ShareStoryModal({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation()
  const panelRef = useRef<HTMLDivElement>(null)
  // Always mounted when rendered — keyboard a11y: focus in, Tab trap, Escape, focus restore.
  useModalA11y(true, onClose, panelRef)
  const [form, setForm] = useState<TestimonialRequest>({
    authorName: '', authorRole: 'PATIENT', location: '', title: '', body: '',
    isAnonymous: false, consentGiven: false,
  })
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [submitting, setSubmitting] = useState(false)
  const [serverError, setServerError] = useState<string | null>(null)
  const [done, setDone] = useState(false)

  const submit = async (ev: React.FormEvent) => {
    ev.preventDefault()
    setServerError(null)
    const e: Record<string, string> = {}
    if (!form.title.trim()) e.title = t('pages.stories.modal.errTitle')
    if (!form.body.trim() || form.body.trim().length < 20) e.body = t('pages.stories.modal.errBody')
    if (!form.consentGiven) e.consent = t('pages.stories.modal.errConsent')
    setErrors(e)
    if (Object.keys(e).length > 0) return
    setSubmitting(true)
    try {
      await submitTestimonial({
        authorName: form.isAnonymous ? undefined : (form.authorName?.trim() || undefined),
        authorRole: form.authorRole,
        location: form.isAnonymous ? undefined : (form.location?.trim() || undefined),
        title: form.title.trim(),
        body: form.body.trim(),
        isAnonymous: form.isAnonymous,
        consentGiven: true,
      })
      setDone(true)
    } catch (err: any) {
      setServerError(err?.response?.data?.message || t('pages.stories.modal.errServer'))
    } finally { setSubmitting(false) }
  }

  const roleLabel = (r: TestimonialRole) =>
    t(`pages.stories.roles.${r}`, { defaultValue: TESTIMONIAL_ROLE_LABELS[r] })

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true" aria-label={t('pages.stories.modal.ariaLabel')} onClick={onClose}>
      <div ref={panelRef} tabIndex={-1} className="bg-white rounded-xl max-w-lg w-full max-h-[90vh] overflow-auto p-6 outline-none" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <h2 className="text-xl font-bold text-snow-900">{t('pages.stories.modal.title')}</h2>
            <p className="text-sm text-snow-500 mt-1">{t('pages.stories.modal.subtitle')}</p>
          </div>
          <button onClick={onClose} className="text-snow-500 hover:text-snow-700 p-1" aria-label={t('pages.stories.modal.close')}><X className="w-5 h-5" /></button>
        </div>

        {done ? (
          <div className="text-center py-8">
            <CheckCircle2 className="w-12 h-12 text-[rgb(0_168_107)] mx-auto mb-4" aria-hidden="true" />
            <h3 className="text-lg font-bold text-snow-900">{t('pages.stories.modal.successTitle')}</h3>
            <p className="text-snow-600 mt-2 max-w-sm mx-auto text-sm">
              {t('pages.stories.modal.successBody')}
            </p>
            <button onClick={onClose} className="btn-outline btn-sm mt-5">{t('pages.stories.modal.closeBtn')}</button>
          </div>
        ) : (
          <form onSubmit={submit} noValidate>
            {serverError && (
              <div className="mb-4 flex items-start gap-2 p-3 rounded-lg bg-red-50 text-red-800 text-sm" role="alert"><AlertCircle className="w-4 h-4 shrink-0 mt-0.5" /><span>{serverError}</span></div>
            )}

            <div className="grid sm:grid-cols-2 gap-4">
              <FormField label={t('pages.stories.modal.roleLabel')} htmlFor="ss-role">
                <select id="ss-role" value={form.authorRole} onChange={(e) => setForm({ ...form, authorRole: e.target.value as TestimonialRole })} className="select">
                  {ROLES.map((r) => <option key={r} value={r}>{roleLabel(r)}</option>)}
                </select>
              </FormField>
              <FormField label={t('pages.stories.modal.nameLabel')} htmlFor="ss-name">
                <input id="ss-name" type="text" value={form.authorName ?? ''} disabled={form.isAnonymous} onChange={(e) => setForm({ ...form, authorName: e.target.value })} className="input disabled:opacity-50" placeholder={form.isAnonymous ? t('pages.stories.modal.namePlaceholderHidden') : t('pages.stories.modal.namePlaceholderShown')} />
              </FormField>
            </div>

            <div className="mt-4">
              <FormField label={t('pages.stories.modal.locLabel')} htmlFor="ss-loc">
                <input id="ss-loc" type="text" value={form.location ?? ''} disabled={form.isAnonymous} onChange={(e) => setForm({ ...form, location: e.target.value })} className="input disabled:opacity-50" placeholder={form.isAnonymous ? t('pages.stories.modal.locPlaceholderHidden') : t('pages.stories.modal.locPlaceholderShown')} />
              </FormField>
            </div>

            <div className="mt-4">
              <FormField label={t('pages.stories.modal.titleLabel')} htmlFor="ss-title" required error={errors.title}>
                <input id="ss-title" type="text" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={`input ${errors.title ? 'input-error' : ''}`} aria-invalid={!!errors.title} />
              </FormField>
            </div>

            <div className="mt-4">
              <FormField label={t('pages.stories.modal.bodyLabel')} htmlFor="ss-body" required error={errors.body} helpText={t('pages.stories.modal.bodyHelp')}>
                <textarea id="ss-body" rows={5} required value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} className={`input ${errors.body ? 'input-error' : ''}`} aria-invalid={!!errors.body} />
              </FormField>
            </div>

            <label className="flex items-center gap-2 mt-4 text-sm text-snow-700">
              <input type="checkbox" checked={Boolean(form.isAnonymous)} onChange={(e) => setForm({ ...form, isAnonymous: e.target.checked })} />
              {t('pages.stories.modal.anonymousLabel')}
            </label>

            <label className="flex items-start gap-2 mt-3 text-sm text-snow-700">
              <input type="checkbox" checked={form.consentGiven} onChange={(e) => setForm({ ...form, consentGiven: e.target.checked })} className="mt-0.5" aria-invalid={!!errors.consent} />
              <span>{t('pages.stories.modal.consentLabel')} {errors.consent && <span className="text-red-600 block">{errors.consent}</span>}</span>
            </label>

            <div className="mt-5 flex items-center gap-3">
              <button type="submit" disabled={submitting} className="btn-primary inline-flex items-center gap-2">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} {submitting ? t('pages.stories.modal.submitting') : t('pages.stories.modal.submit')}
              </button>
              <button type="button" onClick={onClose} className="btn-ghost">{t('pages.stories.modal.cancel')}</button>
            </div>
          </form>
        )}
      </div>
    </div>
  )
}